import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Space, Table, Tag, Drawer, Typography, Descriptions, Alert, Button } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { SubscriptionBillingRunListItem, SubscriptionBillingRunStatus } from '@mygate/common';
import { subscriptionService } from '@/services/subscription';
import { ColumnsType } from 'antd/es/table';
import { formatDateTime } from '@/utils/date';
import { formatCurrency } from '@/utils/currency';

const { Text, Title } = Typography;

/**
 * Journal of automatic monthly billing runs (MYG-503): one row per billing
 * month with counters and per-inquiry approval errors. A month stuck in
 * RUNNING means the last pass had approval errors and the next daily tick
 * will retry the leftovers.
 */
export function AutoBillingRunsTab() {
  const [selectedRun, setSelectedRun] = useState<SubscriptionBillingRunListItem | null>(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const {
    data: runsData,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['subscription-billing-runs', page, pageSize],
    queryFn: async () => {
      const response = await subscriptionService.listBillingRuns(page, pageSize);
      return response.data;
    },
    // No polling: the journal changes at most once a day (the 08:30 UTC tick);
    // react-query already refetches on mount/focus and the error Alert has a
    // manual Retry.
  });

  const columns: ColumnsType<SubscriptionBillingRunListItem> = useMemo(
    () => [
      {
        title: 'Month',
        dataIndex: 'billingMonth',
        key: 'billingMonth',
        width: 100,
        render: (month: string) => <Text strong>{month}</Text>,
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 120,
        render: (status: SubscriptionBillingRunStatus) =>
          status === SubscriptionBillingRunStatus.COMPLETED ? (
            <Tag color="green">COMPLETED</Tag>
          ) : (
            <Tag color="orange">RUNNING</Tag>
          ),
      },
      {
        title: 'Laufzeit',
        key: 'duration',
        width: 200,
        render: (_, record) => (
          <div className="table-cell-stack">
            <Text>Start: {formatDateTime(record.startedAt)}</Text>
            <Text className="table-cell-muted">
              Ende: {record.finishedAt ? formatDateTime(record.finishedAt) : '—'}
            </Text>
          </div>
        ),
      },
      {
        title: 'Considered',
        dataIndex: 'subscriptionsConsidered',
        key: 'subscriptionsConsidered',
        width: 110,
        align: 'right',
      },
      {
        title: 'Generated',
        dataIndex: 'generated',
        key: 'generated',
        width: 100,
        align: 'right',
        render: (count?: number) => (
          <Text strong style={{ color: count ? '#52c41a' : undefined }}>
            {count ?? 0}
          </Text>
        ),
      },
      {
        title: 'Approved',
        dataIndex: 'approved',
        key: 'approved',
        width: 100,
        align: 'right',
      },
      {
        title: 'Errors',
        key: 'errors',
        width: 80,
        align: 'right',
        render: (_, record) => {
          const count = record.approvalErrors?.length ?? 0;
          return <Text style={{ color: count > 0 ? '#ff4d4f' : undefined }}>{count}</Text>;
        },
      },
      {
        title: 'Amount (EUR)',
        dataIndex: 'totalGeneratedAmount',
        key: 'totalGeneratedAmount',
        width: 130,
        align: 'right',
        render: (amount?: number) => formatCurrency(amount),
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 80,
        render: (_, record) =>
          (record.approvalErrors?.length ?? 0) > 0 ? (
            <Button
              type="link"
              className="table-cell-link"
              onClick={() => {
                setSelectedRun(record);
                setDrawerVisible(true);
              }}
            >
              Details
            </Button>
          ) : null,
      },
    ],
    [],
  );

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      {isError && (
        <Alert
          message="Failed to load billing runs"
          type="error"
          showIcon
          action={
            <Button size="small" icon={<ReloadOutlined />} onClick={() => refetch()}>
              Retry
            </Button>
          }
        />
      )}

      <Alert
        message="Automatic monthly billing"
        description="Runs daily at 08:30 UTC; bills the current month once, on or after its first business day. A month stuck in RUNNING had generation/approval errors — the next daily tick retries the leftovers, but only until the month ends. A run still RUNNING after its month is over will NOT retry automatically and needs manual review (approve or cancel its pending charges in the Charges tab)."
        type="info"
        showIcon
      />

      <Table
        scroll={{ x: 1020 }}
        columns={columns}
        dataSource={runsData?.items ?? []}
        rowKey="id"
        loading={isLoading}
        pagination={{
          current: page,
          pageSize,
          total: runsData?.total ?? 0,
          showTotal: total => `Total ${total} runs`,
          // Fixed page size: antd auto-shows the size changer once total > 50,
          // but the fetch is keyed to the constant pageSize — disable it.
          showSizeChanger: false,
          onChange: p => setPage(p),
        }}
      />

      <Drawer
        title={`Errors — ${selectedRun?.billingMonth ?? ''}`}
        open={drawerVisible}
        onClose={() => {
          setDrawerVisible(false);
          setSelectedRun(null);
        }}
        width={600}
      >
        {selectedRun && (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Descriptions column={{ xs: 1, sm: 2, md: 2, lg: 2, xl: 2, xxl: 2 }} bordered size="small">
              <Descriptions.Item label="Month">{selectedRun.billingMonth}</Descriptions.Item>
              <Descriptions.Item label="Status">{selectedRun.status}</Descriptions.Item>
              <Descriptions.Item label="Considered">{selectedRun.subscriptionsConsidered ?? 0}</Descriptions.Item>
              <Descriptions.Item label="Approved">{selectedRun.approved ?? 0}</Descriptions.Item>
            </Descriptions>

            <Title level={5}>Errors ({selectedRun.approvalErrors?.length ?? 0})</Title>
            <Table
              scroll={{ x: 'max-content' }}
              dataSource={selectedRun.approvalErrors ?? []}
              rowKey={(_, idx) => String(idx)}
              size="small"
              pagination={false}
              columns={[
                {
                  title: 'Reference',
                  key: 'reference',
                  width: 220,
                  ellipsis: true,
                  // Approval errors reference the charge inquiry; generation
                  // errors reference the subscription (no inquiry was created)
                  render: (_, record) =>
                    record.chargeInquiryId ?? (record.subscriptionId ? `subscription ${record.subscriptionId}` : '-'),
                },
                {
                  title: 'Error',
                  dataIndex: 'error',
                  key: 'error',
                },
              ]}
            />
          </Space>
        )}
      </Drawer>
    </Space>
  );
}
